import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { date, dateTime, eur, FIELD_LABEL, iban } from '../lib/format';
import { useSession } from '../lib/session';
import { Badge, Confirm, ErrorBox, Field, Loading, Modal, Severity, StatusBadge, Tabs, useAction, useLoad } from '../components/ui';

type Inv = any;

const num = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const fmtNum = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v).replace('.', ','));

function NumInput({ value, onChange, disabled, ...rest }: { value: number | null; onChange: (v: number | null) => void; disabled?: boolean; [k: string]: any }) {
  const [text, setText] = useState(fmtNum(value));
  useEffect(() => { setText(fmtNum(value)); }, [value]);
  return <input inputMode="decimal" disabled={disabled} value={text} onChange={(e) => setText(e.target.value)} onBlur={() => onChange(num(text))} className="num" {...rest} />;
}

export function InvoiceDetail() {
  const { id } = useParams();
  const s = useSession();
  const nav = useNavigate();
  const { busy, run } = useAction();
  const { data: inv, error, loading, reload } = useLoad<Inv>(() => api.get(`/invoices/${id}`), [id]);
  const [form, setForm] = useState<any>(null);
  const [account, setAccount] = useState<string>('');
  const [costCenter, setCostCenter] = useState<string>('');
  const [lineAcc, setLineAcc] = useState<Array<{ account: string; costCenter: string }>>([]);
  const [tab, setTab] = useState<'datos' | 'contable' | 'historial' | 'comunicacion'>('datos');
  const [modal, setModal] = useState<null | 'reject' | 'cancel' | 'justify' | 'approve'>(null);
  const masters = useLoad(async () => {
    if (!inv?.company_id) return { accounts: [], ccs: [] };
    const [accounts, ccs] = await Promise.all([api.get(`/companies/${inv.company_id}/accounts`), api.get(`/companies/${inv.company_id}/cost-centers`)]);
    return { accounts, ccs };
  }, [inv?.company_id]);

  useEffect(() => {
    if (!inv) return;
    setForm(inv.data && Object.keys(inv.data).length ? JSON.parse(JSON.stringify(inv.data)) : null);
    setAccount(inv.account ?? '');
    setCostCenter(inv.cost_center ?? '');
    setLineAcc((inv.lines ?? []).map((l: any) => ({ account: l.account ?? '', costCenter: l.cost_center ?? '' })));
  }, [inv]);

  // Recarga automática mientras se está leyendo
  useEffect(() => {
    if (inv && ['received', 'processing'].includes(inv.status)) {
      const t = setTimeout(reload, 2500);
      return () => clearTimeout(t);
    }
  }, [inv, reload]);

  const editable = inv && ['review', 'pending_approval', 'rejected', 'received'].includes(inv.status) && s.can('invoice.edit');
  const dirty = useMemo(() => inv && form && (JSON.stringify(form) !== JSON.stringify(inv.data) || account !== (inv.account ?? '') || costCenter !== (inv.cost_center ?? '') || JSON.stringify(lineAcc) !== JSON.stringify((inv.lines ?? []).map((l: any) => ({ account: l.account ?? '', costCenter: l.cost_center ?? '' })))), [inv, form, account, costCenter, lineAcc]);

  if (loading && !inv) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  if (!inv) return null;

  const results: any[] = inv.rule_results ?? [];
  const errors = results.filter((r) => r.severity === 'error');
  const warnings = results.filter((r) => r.severity === 'warning');
  const infos = results.filter((r) => r.severity === 'info');
  const pendingApproval = inv.approvals?.find((a: any) => a.status === 'pending');
  const ev = (field: string) => inv.evidence?.[field];

  const save = async () => {
    const r = await run(() => api.patch(`/invoices/${id}`, { data: form ?? undefined, account: account || null, costCenter: costCenter || null, lines: lineAcc.map((l) => ({ account: l.account || null, costCenter: l.costCenter || null })) }), 'Cambios guardados y revalidados');
    if (r) reload();
    return r;
  };
  const submit = async (justification?: string) => {
    if (dirty && !(await save())) return;
    if (await run(() => api.post(`/invoices/${id}/submit`, { justification }), 'Enviada a aprobación')) { setModal(null); reload(); }
  };
  const upd = (path: string, value: unknown) => {
    const next = JSON.parse(JSON.stringify(form));
    const keys = path.split('.');
    let o = next;
    for (const k of keys.slice(0, -1)) o = o[k];
    o[keys[keys.length - 1]] = value;
    setForm(next);
  };

  return (
    <>
      <div className="row between wrap" style={{ marginBottom: 12 }}>
        <div>
          <Link to="/" className="muted">← Bandeja</Link>
          <h1>{inv.vendor_name ?? inv.document?.filename ?? 'Factura'} {inv.number && <span className="muted mono">· {inv.number}</span>}</h1>
          <div className="row wrap">
            <StatusBadge status={inv.status} />
            {inv.touchless && <Badge tone="ok">Aprobada sin intervención</Badge>}
            {inv.einvoice_format && <Badge tone="ok">Factura electrónica ({inv.einvoice_format})</Badge>}
            {inv.extraction?.source === 'ai' && <Badge tone="info">Leída con IA · {inv.extraction?.model}</Badge>}
            {inv.extraction?.source === 'heuristic' && <Badge tone="warn">Lectura por patrones de texto</Badge>}
            {inv.extraction?.confidence !== undefined && <Badge>Confianza {Math.round(inv.extraction.confidence * 100)} %</Badge>}
            <strong style={{ fontSize: 18 }}>{eur(inv.total)}</strong>
          </div>
        </div>
        <div className="row wrap">
          {editable && <button disabled={busy || !dirty} onClick={save}>Guardar cambios</button>}
          {['review', 'rejected'].includes(inv.status) && s.can('invoice.submit') && (
            <button className="primary" disabled={busy} onClick={() => (errors.length ? setModal('justify') : submit())}>Enviar a aprobación</button>
          )}
          {inv.status === 'pending_approval' && s.can('invoice.approve') && <button className="ok" disabled={busy} onClick={() => setModal('approve')}>Aprobar</button>}
          {['review', 'pending_approval', 'approved'].includes(inv.status) && s.can('invoice.reject') && <button className="danger" disabled={busy} onClick={() => setModal('reject')}>Rechazar</button>}
          {['review', 'rejected', 'received'].includes(inv.status) && s.can('invoice.cancel') && <button className="ghost" disabled={busy} onClick={() => setModal('cancel')}>Anular</button>}
          {['pending_approval', 'approved', 'rejected'].includes(inv.status) && s.can('invoice.edit') && <button className="ghost" disabled={busy} onClick={async () => { if (await run(() => api.post(`/invoices/${id}/reopen`), 'Devuelta a revisión')) reload(); }}>Volver a revisión</button>}
        </div>
      </div>

      {['received', 'processing'].includes(inv.status) && <div className="alert info">Leyendo el documento… la página se actualizará sola.</div>}
      {inv.status === 'pending_approval' && pendingApproval && <div className="alert info">Pendiente del nivel {pendingApproval.level} de aprobación{pendingApproval.approver_name ? ` (${pendingApproval.approver_name})` : pendingApproval.approver_role ? ` (rol ${pendingApproval.approver_role})` : ''} desde {dateTime(pendingApproval.requested_at)}.</div>}

      {(errors.length > 0 || warnings.length > 0) && (
        <div className="card flat stack" style={{ marginBottom: 12 }}>
          <h3>Por qué está en revisión</h3>
          {[...errors, ...warnings].map((r, i) => (
            <div key={i} className={`alert ${r.severity === 'error' ? 'error' : 'warn'}`}>
              <Severity s={r.severity} />
              <div><div>{r.message}</div>{r.legal && <div className="legal">{r.legal}</div>}</div>
            </div>
          ))}
        </div>
      )}

      <div className="split">
        <div className="viewer">
          {inv.document?.file_deleted_at ? (
            <div className="empty">El original se borró de Conforme el {dateTime(inv.document.file_deleted_at)} al cumplirse el plazo de conservación. Se conservan los datos leídos y la huella del documento.</div>
          ) : inv.document ? (
            inv.document.mime === 'application/pdf' ? <iframe title="Documento" src={`/api/documents/${inv.document.id}/file#view=FitH`} />
              : inv.document.mime.startsWith('image/') ? <img alt="Documento original" src={`/api/documents/${inv.document.id}/file`} />
                : <XmlView documentId={inv.document.id} />
          ) : <div className="empty">Sin documento</div>}
        </div>

        <div className="stack">
          <Tabs value={tab} onChange={setTab} items={[{ id: 'datos', label: 'Datos' }, { id: 'contable', label: 'Contabilización' }, { id: 'historial', label: 'Historial' }, { id: 'comunicacion', label: 'Proveedor' }]} />

          {tab === 'datos' && (form ? (
            <div className="card flat stack">
              <div className="grid2">
                <Field label="Proveedor" hint={<Evidence e={ev('supplier.name')} />}><input disabled={!editable} value={form.supplier.name ?? ''} onChange={(e) => upd('supplier.name', e.target.value || null)} /></Field>
                <Field label="NIF del proveedor" hint={<Evidence e={ev('supplier.nif')} />}><input disabled={!editable} className="mono" value={form.supplier.nif ?? ''} onChange={(e) => upd('supplier.nif', e.target.value || null)} /></Field>
                <Field label="Número" hint={<Evidence e={ev('invoice_number')} />}><input disabled={!editable} className="mono" value={form.invoice_number ?? ''} onChange={(e) => upd('invoice_number', e.target.value || null)} /></Field>
                <Field label="Serie"><input disabled={!editable} className="mono" value={form.series ?? ''} onChange={(e) => upd('series', e.target.value || null)} /></Field>
                <Field label="Fecha de expedición" hint={<Evidence e={ev('issue_date')} />}><input type="date" disabled={!editable} value={form.issue_date ?? ''} onChange={(e) => upd('issue_date', e.target.value || null)} /></Field>
                <Field label="Vencimiento"><input type="date" disabled={!editable} value={form.due_date ?? ''} onChange={(e) => upd('due_date', e.target.value || null)} /></Field>
                <Field label="Tipo de documento">
                  <select disabled={!editable} value={form.document_type} onChange={(e) => upd('document_type', e.target.value)}>
                    <option value="invoice">Factura</option><option value="simplified_invoice">Factura simplificada</option><option value="rectifying_invoice">Factura rectificativa</option>
                    <option value="delivery_note">Albarán</option><option value="proforma">Proforma</option><option value="receipt">Recibo / ticket</option><option value="other">Otro</option>
                  </select>
                </Field>
                <Field label="NIF del destinatario" hint={<Evidence e={ev('customer.nif')} />}><input disabled={!editable} className="mono" value={form.customer.nif ?? ''} onChange={(e) => upd('customer.nif', e.target.value || null)} /></Field>
              </div>
              {form.document_type === 'rectifying_invoice' && (
                <div className="grid2">
                  <Field label="Factura que rectifica"><input disabled={!editable} value={form.references.rectified_invoice ?? ''} onChange={(e) => upd('references.rectified_invoice', e.target.value || null)} /></Field>
                  <Field label="Motivo de la rectificación"><input disabled={!editable} value={form.references.rectification_reason ?? ''} onChange={(e) => upd('references.rectification_reason', e.target.value || null)} /></Field>
                </div>
              )}
              <h3>Impuestos</h3>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Régimen</th><th className="num">Tipo %</th><th className="num">Base</th><th className="num">Cuota</th><th className="num">Recargo %</th><th className="num">Recargo</th>{editable && <th />}</tr></thead>
                  <tbody>
                    {form.taxes.map((t: any, i: number) => (
                      <tr key={i}>
                        <td><select disabled={!editable} value={t.regime} onChange={(e) => upd(`taxes.${i}.regime`, e.target.value)}>{['IVA', 'IGIC', 'IPSI', 'EXENTO', 'NO_SUJETO', 'ISP', 'INTRACOMUNITARIA'].map((r) => <option key={r}>{r}</option>)}</select></td>
                        <td><NumInput disabled={!editable} value={t.rate} onChange={(v) => upd(`taxes.${i}.rate`, v ?? 0)} /></td>
                        <td><NumInput disabled={!editable} value={t.base} onChange={(v) => upd(`taxes.${i}.base`, v ?? 0)} /></td>
                        <td><NumInput disabled={!editable} value={t.amount} onChange={(v) => upd(`taxes.${i}.amount`, v ?? 0)} /></td>
                        <td><NumInput disabled={!editable} value={t.surcharge_rate} onChange={(v) => upd(`taxes.${i}.surcharge_rate`, v)} /></td>
                        <td><NumInput disabled={!editable} value={t.surcharge_amount} onChange={(v) => upd(`taxes.${i}.surcharge_amount`, v)} /></td>
                        {editable && <td><button className="ghost small" aria-label="Quitar tramo" onClick={() => setForm({ ...form, taxes: form.taxes.filter((_: any, j: number) => j !== i) })}>✕</button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {editable && <button className="small" onClick={() => setForm({ ...form, taxes: [...form.taxes, { regime: 'IVA', rate: 21, base: 0, amount: 0, surcharge_rate: null, surcharge_amount: null }] })}>+ Añadir tramo</button>}
              <div className="grid4">
                <Field label="Base total" hint={<Evidence e={ev('totals.base')} />}><NumInput disabled={!editable} value={form.totals.base} onChange={(v) => upd('totals.base', v)} /></Field>
                <Field label="Impuestos" hint={<Evidence e={ev('totals.tax')} />}><NumInput disabled={!editable} value={form.totals.tax} onChange={(v) => upd('totals.tax', v)} /></Field>
                <Field label="Retención IRPF (importe)"><NumInput disabled={!editable} value={form.withholding.amount} onChange={(v) => { upd('withholding.amount', v); }} /></Field>
                <Field label="Total" hint={<Evidence e={ev('totals.total')} />}><NumInput disabled={!editable} value={form.totals.total} onChange={(v) => upd('totals.total', v)} /></Field>
              </div>
              <div className="grid2">
                <Field label="% retención"><NumInput disabled={!editable} value={form.withholding.rate} onChange={(v) => upd('withholding.rate', v)} /></Field>
                <Field label="IBAN para el pago" hint={<><Evidence e={ev('iban')} />{inv.vendor?.iban_verified && <span> · Verificado del proveedor: <span className="mono">{iban(inv.vendor.iban_verified)}</span></span>}</>}>
                  <input disabled={!editable} className="mono" value={form.payment.iban ?? ''} onChange={(e) => upd('payment.iban', e.target.value.replace(/\s/g, '') || null)} />
                </Field>
                <Field label="Pedido"><input disabled={!editable} value={form.references.purchase_order ?? ''} onChange={(e) => upd('references.purchase_order', e.target.value || null)} /></Field>
                <Field label="Albaranes (separados por comas)"><input disabled={!editable} value={(form.references.delivery_notes ?? []).join(', ')} onChange={(e) => upd('references.delivery_notes', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} /></Field>
              </div>
              {form.lines.length > 0 && (
                <>
                  <h3>Líneas</h3>
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Descripción</th><th className="num">Cant.</th><th className="num">Precio</th><th className="num">Importe</th><th className="num">% imp.</th><th>Cuenta</th><th>C. coste</th></tr></thead>
                      <tbody>
                        {form.lines.map((l: any, i: number) => (
                          <tr key={i}>
                            <td style={{ minWidth: 180 }}>{l.description}{l.delivery_note && <div className="hint">Albarán {l.delivery_note}</div>}</td>
                            <td className="num">{l.quantity ?? '—'}</td><td className="num">{l.unit_price ?? '—'}</td><td className="num">{eur(l.net_amount)}</td><td className="num">{l.tax_rate ?? '—'}</td>
                            <td><input list="accounts" disabled={!editable} style={{ width: 96 }} className="mono" value={lineAcc[i]?.account ?? ''} onChange={(e) => setLineAcc(lineAcc.map((x, j) => (j === i ? { ...x, account: e.target.value } : x)))} /></td>
                            <td><input list="ccs" disabled={!editable} style={{ width: 80 }} value={lineAcc[i]?.costCenter ?? ''} onChange={(e) => setLineAcc(lineAcc.map((x, j) => (j === i ? { ...x, costCenter: e.target.value } : x)))} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {inv.corrected_fields?.length > 0 && <p className="hint">Campos corregidos por una persona: {inv.corrected_fields.map((f: string) => FIELD_LABEL[f] ?? f).join(', ')}.</p>}
              {infos.length > 0 && <div className="stack">{infos.map((r, i) => <div key={i} className="alert info"><Severity s="info" /><div>{r.message}{r.legal && <div className="legal">{r.legal}</div>}</div></div>)}</div>}
            </div>
          ) : (
            <div className="card flat">
              <p>No hay datos leídos de este documento.</p>
              {editable && <button onClick={() => setForm(blankInvoice())}>Introducir los datos a mano</button>}
            </div>
          ))}

          {tab === 'contable' && (
            <div className="card flat stack">
              <div className="grid2">
                <Field label="Cuenta de gasto" hint={inv.vendor?.memory?.accounts ? `Aprendido de este proveedor: ${Object.entries(inv.vendor.memory.accounts).map(([k, n]) => `${k} (${n})`).join(', ')}` : 'Se aprende al aprobar'}>
                  <input list="accounts" className="mono" disabled={!editable} value={account} onChange={(e) => setAccount(e.target.value)} />
                </Field>
                <Field label="Centro de coste"><input list="ccs" disabled={!editable} value={costCenter} onChange={(e) => setCostCenter(e.target.value)} /></Field>
              </div>
              {inv.vendor && <p className="hint">Subcuenta del proveedor: <span className="mono">{inv.vendor.ledger_account ?? 'se asignará al aprobar'}</span> · <Link to={`/proveedores/${inv.vendor.id}`}>Ficha del proveedor</Link></p>}
              {inv.entry && (
                <>
                  <h3>Asiento propuesto {inv.entry.balanced ? <Badge tone="ok">Cuadrado</Badge> : <Badge tone="error">Descuadrado</Badge>}</h3>
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Cuenta</th><th>Concepto</th><th className="num">Debe</th><th className="num">Haber</th></tr></thead>
                      <tbody>{inv.entry.lines.map((l: any, i: number) => <tr key={i}><td className="mono">{l.account}</td><td>{l.description}</td><td className="num">{l.debit ? eur(l.debit) : ''}</td><td className="num">{l.credit ? eur(l.credit) : ''}</td></tr>)}</tbody>
                    </table>
                  </div>
                  <p className="hint">Se recalcula al guardar. Las cuentas exactas dependen de la configuración contable de la empresa.</p>
                </>
              )}
            </div>
          )}

          {tab === 'historial' && <History inv={inv} onComment={reload} />}
          {tab === 'comunicacion' && <SupplierTab inv={inv} onChange={reload} />}
        </div>
      </div>

      <datalist id="accounts">{(masters.data?.accounts ?? []).map((a: any) => <option key={a.code} value={a.code}>{a.name}</option>)}</datalist>
      <datalist id="ccs">{(masters.data?.ccs ?? []).map((c: any) => <option key={c.code} value={c.code}>{c.name}</option>)}</datalist>

      {modal === 'reject' && <Confirm title="Rechazar factura" danger needReason reasons={inv.rejectionReasons} reasonLabel="Motivo del rechazo (se comunica al proveedor si es factura electrónica)"
        onClose={() => setModal(null)} onConfirm={async (reason) => { if (await run(() => api.post(`/invoices/${id}/reject`, { reason, notifySupplier: true }), 'Factura rechazada. Hay un borrador de correo al proveedor en la pestaña «Proveedor».')) { setModal(null); reload(); } }} />}
      {modal === 'cancel' && <Confirm title="Anular factura" danger needReason text="La factura queda anulada (por ejemplo, un duplicado o un documento que no es una factura). Queda registrada en el historial." onClose={() => setModal(null)}
        onConfirm={async (reason) => { if (await run(() => api.post(`/invoices/${id}/cancel`, { reason }), 'Factura anulada')) { setModal(null); nav('/'); } }} />}
      {modal === 'justify' && <Confirm title="Enviar a aprobación con incidencias" needReason reasonLabel="Justificación (queda en el historial y la ve quien aprueba)"
        text={<>Esta factura tiene {errors.length} error(es). Si estás seguro de que es correcta, explica por qué.</>} onClose={() => setModal(null)} onConfirm={(r) => submit(r)} />}
      {modal === 'approve' && <ApproveModal onClose={() => setModal(null)} onConfirm={async (comment) => { if (await run(() => api.post(`/invoices/${id}/approve`, { comment }), 'Factura aprobada')) { setModal(null); reload(); } }} warnings={[...errors, ...warnings]} total={inv.total} />}
    </>
  );
}

function ApproveModal({ onClose, onConfirm, warnings, total }: { onClose: () => void; onConfirm: (c: string) => void; warnings: any[]; total: number }) {
  const [comment, setComment] = useState('');
  return (
    <Modal title={`Aprobar ${eur(total)}`} onClose={onClose} footer={<><button onClick={onClose}>Cancelar</button><button className="ok" onClick={() => onConfirm(comment)}>Aprobar</button></>}>
      {warnings.length > 0 && <div className="alert warn">Esta factura tenía incidencias: {warnings.map((w) => w.message).join(' · ')}</div>}
      <Field label="Comentario (opcional)"><textarea value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
    </Modal>
  );
}

function Evidence({ e }: { e?: { quote?: string; anchored?: boolean } }) {
  if (!e) return null;
  return <span className={`evidence ${e.anchored === false ? 'bad' : ''}`}>{e.anchored === false ? '⚠ No localizado en el documento · ' : 'Leído: '}«{(e.quote ?? '').slice(0, 60)}»</span>;
}

function XmlView({ documentId }: { documentId: string }) {
  const { data } = useLoad(async () => (await fetch(`/api/documents/${documentId}/file`)).text(), [documentId]);
  return <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{data ?? 'Cargando…'}</pre>;
}

function History({ inv, onComment }: { inv: any; onComment: () => void }) {
  const [text, setText] = useState('');
  const { busy, run } = useAction();
  const ACTIONS: Record<string, string> = {
    'invoice.extracted': 'Leída', 'invoice.edited': 'Editada', 'invoice.submitted': 'Enviada a aprobación', 'approval.approved': 'Aprobación', 'approval.rejected': 'Rechazo en aprobación',
    'invoice.approved': 'Aprobada', 'invoice.auto_approved': 'Aprobada automáticamente', 'invoice.rejected': 'Rechazada', 'invoice.cancelled': 'Anulada', 'invoice.reopened': 'Devuelta a revisión',
    'invoice.paid': 'Pagada', 'invoice.justified': 'Justificación de incidencias', 'einvoice.status_created': 'Estado de factura electrónica', 'document.viewed': 'Documento consultado',
  };
  return (
    <div className="stack">
      <div className="card flat">
        <h3>Aprobaciones</h3>
        {inv.approvals.length === 0 ? <p className="muted">Sin circuito de aprobación todavía.</p> : (
          <ul className="timeline">{inv.approvals.map((a: any) => (
            <li key={a.id}><span className="dot" style={{ background: a.status === 'approved' ? 'var(--accent)' : a.status === 'rejected' ? 'var(--error)' : '#c5ccc8' }} />
              <div>Nivel {a.level} · {a.approver_name ?? (a.approver_role ? `rol ${a.approver_role}` : '—')} · <strong>{({ waiting: 'en espera', pending: 'pendiente', approved: 'aprobado', rejected: 'rechazado', skipped: 'omitido' } as any)[a.status]}</strong>
                {a.decided_at && <span className="muted"> · {a.decided_by_name ?? 'enlace'} · {dateTime(a.decided_at)}</span>}{a.comment && <div className="hint">«{a.comment}»</div>}</div></li>
          ))}</ul>
        )}
      </div>
      {inv.einvoiceEvents.length > 0 && (
        <div className="card flat">
          <h3>Estados legales de la factura electrónica</h3>
          <ul className="timeline">{inv.einvoiceEvents.map((e: any) => (
            <li key={e.id}><span className="dot" /><div>{({ accepted: 'Aceptada', rejected: 'Rechazada', paid: 'Pagada', partially_paid: 'Pago parcial' } as any)[e.status]} el {date(e.event_date)} · comunicar antes del {date(e.due_by)} ·{' '}
              <Badge tone={e.submission_status === 'acknowledged' ? 'ok' : e.deadline === 'overdue' ? 'error' : 'warn'}>{({ acknowledged: 'Comunicado', pending: 'Pendiente', manual: 'Pendiente (manual)', error: 'Error', sent: 'Enviado' } as any)[e.submission_status]}</Badge>
              {e.reason && <div className="hint">Motivo: {e.reason}</div>}</div></li>
          ))}</ul>
        </div>
      )}
      <div className="card flat stack">
        <h3>Comentarios</h3>
        {inv.comments.map((c: any) => <div key={c.id}><strong>{c.user_name ?? 'Sistema'}</strong> <span className="muted">{dateTime(c.created_at)}</span><div>{c.body}</div></div>)}
        <textarea placeholder="Escribe un comentario para el equipo…" value={text} onChange={(e) => setText(e.target.value)} />
        <div><button disabled={busy || !text.trim()} onClick={async () => { if (await run(() => api.post(`/invoices/${inv.id}/comments`, { body: text }))) { setText(''); onComment(); } }}>Comentar</button></div>
      </div>
      <div className="card flat">
        <h3>Registro de auditoría</h3>
        <ul className="timeline">{inv.audit.map((a: any) => <li key={a.id}><span className="dot" style={{ background: '#9bb0aa' }} /><div>{ACTIONS[a.action] ?? a.action} · <span className="muted">{a.actor_name ?? (a.actor_type === 'system' ? 'Conforme' : a.actor_type === 'approval_link' ? 'Enlace de aprobación' : a.actor_type)} · {dateTime(a.created_at)}</span></div></li>)}</ul>
        {inv.document && <p className="hint">Original: {inv.document.filename} · SHA-256 <span className="mono">{inv.document.sha256.slice(0, 16)}…</span> · conservado hasta {date(inv.document.retention_until)}</p>}
      </div>
    </div>
  );
}

function SupplierTab({ inv, onChange }: { inv: any; onChange: () => void }) {
  const { busy, run } = useAction();
  const [edit, setEdit] = useState<any>(null);
  const kinds = [['request_rectification', 'Pedir rectificativa'], ['request_missing_data', 'Pedir datos'], ['payment_notice', 'Aviso de pago'], ['iban_verification', 'Verificar cambio de cuenta']] as const;
  return (
    <div className="stack">
      <div className="card flat stack">
        <h3>Escribir al proveedor</h3>
        <p className="hint">Se prepara un borrador con las incidencias detectadas. Nada se envía sin que lo revises.</p>
        <div className="row wrap">{kinds.map(([k, l]) => <button key={k} disabled={busy} onClick={async () => { const m = await run(() => api.post(`/invoices/${inv.id}/messages`, { kind: k })); if (m) { setEdit(m); onChange(); } }}>{l}</button>)}</div>
        <div className="row wrap">
          <button className="ghost" onClick={async () => { const r = await run(() => api.post(`/invoices/${inv.id}/status-link`)); if (r) { await navigator.clipboard?.writeText(r.url).catch(() => {}); alert(`Enlace de estado para el proveedor:\n${r.url}`); onChange(); } }}>Enlace de estado para el proveedor</button>
          {inv.publicStatusUrl && <a href={inv.publicStatusUrl} target="_blank" rel="noreferrer">Ver página de estado</a>}
        </div>
      </div>
      {inv.messages.map((m: any) => (
        <div key={m.id} className="card flat">
          <div className="row between"><strong>{m.subject}</strong><Badge tone={m.status === 'sent' ? 'ok' : m.status === 'draft' ? 'warn' : ''}>{m.status === 'sent' ? 'Enviado' : m.status === 'draft' ? 'Borrador' : 'Descartado'}</Badge></div>
          <div className="hint">Para: {m.to_address ?? 'sin correo'} · {dateTime(m.created_at)}</div>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{m.body}</pre>
          {m.status === 'draft' && <div className="row"><button onClick={() => setEdit(m)}>Revisar y enviar</button><button className="ghost" onClick={async () => { if (await run(() => api.post(`/messages/${m.id}/discard`))) onChange(); }}>Descartar</button></div>}
        </div>
      ))}
      {edit && <MessageEditor m={edit} onClose={() => setEdit(null)} onSent={() => { setEdit(null); onChange(); }} />}
    </div>
  );
}

export function MessageEditor({ m, onClose, onSent }: { m: any; onClose: () => void; onSent: () => void }) {
  const [to, setTo] = useState(m.to_address ?? '');
  const [subject, setSubject] = useState(m.subject);
  const [body, setBody] = useState(m.body);
  const { busy, run } = useAction();
  return (
    <Modal title="Mensaje al proveedor" onClose={onClose} footer={<>
      <button onClick={onClose}>Cerrar</button>
      <button className="primary" disabled={busy || !to} onClick={async () => { if (await run(async () => { await api.patch(`/messages/${m.id}`, { to, subject, body }); return api.post(`/messages/${m.id}/send`); }, 'Mensaje enviado')) onSent(); }}>Enviar</button>
    </>}>
      <Field label="Para"><input type="email" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      <Field label="Asunto"><input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
      <Field label="Mensaje"><textarea style={{ minHeight: 220 }} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
    </Modal>
  );
}

function blankInvoice() {
  return {
    document_type: 'invoice', invoice_number: null, series: null, issue_date: null, operation_date: null, due_date: null, currency: 'EUR',
    supplier: { name: null, nif: null, address: null, country: 'ES', email: null }, customer: { name: null, nif: null }, lines: [],
    taxes: [{ regime: 'IVA', rate: 21, base: 0, amount: 0, surcharge_rate: null, surcharge_amount: null }], withholding: { rate: null, amount: null },
    totals: { base: null, tax: null, surcharge: null, withholding: null, total: null }, payment: { method: 'transfer', iban: null, due_dates: [] },
    references: { purchase_order: null, delivery_notes: [], rectified_invoice: null, rectification_reason: null }, legal_mentions: [], evidence: [], suspicious_instructions: false,
  };
}
